import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { NIGHTS_RANGE_TEXTS } from '../config/texts';
import { formatNightsRange } from '../lib/formatNightsLabel';
import { MAX_NIGHTS, MIN_NIGHTS, setFrom, setTo } from '../lib/nightsRange';
import type { NightsRange } from '@/shared/model';

export interface UseNightsRange {
  from: ComputedRef<number>;
  to: ComputedRef<number>;
  controlActivated: Ref<boolean>;

  selection: ComputedRef<NightsRange>;
  valueLabel: ComputedRef<string>;
  summaryLabel: ComputedRef<string>;

  open: () => void;
  close: () => void;
  toggle: () => void;
  setFromValue: (v: number) => void;
  setToValue: (v: number) => void;
  validate: () => boolean;
}

export interface UseNightsRangeOptions {
  model: Ref<NightsRange>;
  checkInLabel?: string | (() => string | undefined);
}

export function useNightsRange(options: UseNightsRangeOptions): UseNightsRange {
  const model = options.model;
  const getCheckInLabel = (): string => {
    const v = typeof options.checkInLabel === 'function' ? options.checkInLabel() : options.checkInLabel;
    return v ?? '';
  };

  const controlActivated = ref(false);

  const selection = computed<NightsRange>(() => model.value);
  const from = computed<number>(() => model.value.from);
  const to = computed<number>(() => model.value.to);

  const valueLabel = computed<string>(
    () => NIGHTS_RANGE_TEXTS.valuePrefix + formatNightsRange(from.value, to.value, '-'),
  );

  const summaryLabel = computed<string>(
    () =>
      NIGHTS_RANGE_TEXTS.summaryPrefix +
      getCheckInLabel() +
      NIGHTS_RANGE_TEXTS.summaryMid +
      formatNightsRange(from.value, to.value, '–'),
  );

  const open = (): void => {
    controlActivated.value = true;
  };

  const close = (): void => {
    controlActivated.value = false;
  };

  const toggle = (): void => {
    controlActivated.value = !controlActivated.value;
  };

  const setFromValue = (v: number): void => {
    model.value = setFrom(model.value, v);
  };

  const setToValue = (v: number): void => {
    model.value = setTo(model.value, v);
  };

  const validate = (): boolean =>
    model.value.from >= MIN_NIGHTS && model.value.from <= model.value.to && model.value.to <= MAX_NIGHTS;

  return {
    from,
    to,
    controlActivated,
    selection,
    valueLabel,
    summaryLabel,
    open,
    close,
    toggle,
    setFromValue,
    setToValue,
    validate,
  };
}
